import React from 'react';
import { motion } from 'framer-motion';
import { Star, ArrowRight, Palette, ClipboardCheck, CalendarCheck, Bot, Quote, Sparkles } from 'lucide-react';
import { SUCCESS_STORIES } from '../constants';

export const SuccessStories: React.FC = () => {
  return (
    <section id="casos" className="py-24 bg-[#faf8ff] relative overflow-hidden">
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Star size={14} className="text-blue-500 fill-blue-500" />
            Casos Reales
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
            Resultados reales en negocios reales
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Pura automatización, sin intervención humana cada vez. Mira lo que hemos logrado.
          </p>
        </div>

        {/* Stories */}
        <div className="space-y-24">
          {SUCCESS_STORIES.map((story, idx) => {
            const icons = [
              <ClipboardCheck size={20} className="mx-auto text-purple-600 mb-2" />,
              <CalendarCheck size={20} className="mx-auto text-secondary mb-2" />,
              <Bot size={20} className="mx-auto text-primary mb-2" />,
            ];
            const storyImages = story.images ?? [story.image];
            const isReverse = idx % 2 === 1;

            return (
              <motion.div
                key={story.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="group relative bg-white rounded-[2.5rem] shadow-xl hover:shadow-2xl transition-all duration-500 border border-slate-100 overflow-hidden grid lg:grid-cols-2"
              >
                <div className={`p-8 md:p-12 lg:p-16 flex flex-col justify-center order-2 ${isReverse ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="bg-purple-100 p-1.5 rounded-full text-purple-600">
                      {story.icon === 'sparkles' ? <Sparkles size={16} /> : <Palette size={16} />}
                    </div>
                    <span className="text-sm font-bold text-purple-700 uppercase tracking-wide">{story.category}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">
                    {story.name}: <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-secondary">{story.headlineHighlight}</span> {story.headlineSuffix}
                  </h3>
                  <div className="mb-8">
                    <p className="text-slate-600 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: story.description }} />
                    <div className="space-y-3">
                      {story.features.map((feat, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="bg-green-100 p-1 rounded-full text-green-600 mt-0.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </div>
                          <span className="text-sm text-slate-700 font-medium">{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-8">
                    {story.stats.map((stat, i) => (
                      <div key={i} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-center hover:border-secondary/30 transition-colors">
                        {icons[i]}
                        <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                        <div className="text-xs text-slate-500 font-medium uppercase">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-purple-50 p-6 rounded-2xl border border-purple-100">
                    <div className="flex gap-4">
                      <Quote size={24} className="text-purple-300 shrink-0 transform rotate-180" />
                      <div>
                        <p className="text-slate-800 italic text-sm font-medium mb-2">"{story.quote}"</p>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">- {story.quoteAuthor}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`relative h-[400px] lg:h-full overflow-hidden order-1 ${isReverse ? 'lg:order-1' : 'lg:order-2'} bg-slate-800`}>
                  <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-transparent transition-colors z-10" />
                  <div className={`w-full h-full grid ${storyImages.length > 1 ? 'grid-rows-2' : 'grid-rows-1'}`}>
                    {storyImages.slice(0, 2).map((image, imageIdx) => (
                      <img
                        key={image}
                        src={image}
                        alt={`${story.name} - ${imageIdx === 0 ? 'imagen principal' : 'detalle del negocio'}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ))}
                  </div>
                  <div className="absolute top-8 left-8 z-20 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
                    <div className="bg-purple-100 p-1.5 rounded-full text-purple-600">
                      {story.icon === 'sparkles' ? <Sparkles size={16} /> : <Palette size={16} />}
                    </div>
                    <span className="text-sm font-bold text-slate-800 uppercase tracking-wide">Caso Real</span>
                  </div>
                  <div className="absolute bottom-8 right-8 z-20 bg-green-500/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
                    <span className="text-xs font-bold text-white uppercase tracking-wide">{story.badge}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center mt-20">
          <h3 className="text-2xl font-bold text-slate-900 mb-8">¿Listo para ser nuestro próximo caso de éxito?</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#pricing"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-white hover:opacity-90 shadow-lg shadow-primary/30 focus:ring-primary shadow-lg shadow-blue-500/20 px-10 py-4 rounded-full text-lg group"
            >
              Ver precios y planes
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
            </a>
            <button
              onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 border-2 border-slate-200 text-slate-700 bg-transparent hover:border-primary hover:text-primary focus:ring-primary px-10 py-4 rounded-full text-lg hover:bg-slate-100"
            >
              Hablar con un experto
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
